import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import {
  buildDashboard2WeekPickerOptions,
  DASHBOARD2_WEEK_PICKER_STEP,
  isSameDashboard2Week
} from "../utils/dashboard2-week-picker.utils";
import {
  dashboard2WeekPickerCenterLabelSx,
  dashboard2WeekPickerListSx,
  dashboard2WeekPickerNavSx,
  dashboard2WeekPickerOptionSx,
  dashboard2IconTouchTargetSx
} from "../dashboard2-status.styles";

type Dashboard2WeekPickerDialogProps = {
  open: boolean;
  weekAnchor: DateTime;
  onClose: () => void;
  onSelectWeek: (week: DateTime) => void;
};

export const Dashboard2WeekPickerDialog: React.FC<Dashboard2WeekPickerDialogProps> = ({
  open,
  weekAnchor,
  onClose,
  onSelectWeek
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [pickerCenter, setPickerCenter] = useState(weekAnchor);
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setPickerCenter(weekAnchor);
    }
  }, [open, weekAnchor]);

  const weekOptions = useMemo(() => buildDashboard2WeekPickerOptions(pickerCenter), [pickerCenter]);

  const scrollTargetWeekKey = useMemo(() => {
    const selectedInList = weekOptions.find(week => isSameDashboard2Week(week, weekAnchor));
    const targetWeek = selectedInList ?? pickerCenter.startOf("week");

    return targetWeek.toISODate() ?? targetWeek.toISO();
  }, [pickerCenter, weekAnchor, weekOptions]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const scrollSelectedWeekIntoView = () => {
      scrollTargetRef.current?.scrollIntoView({ block: "center", inline: "nearest" });
    };

    const frameId = requestAnimationFrame(scrollSelectedWeekIntoView);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [open, scrollTargetWeekKey, weekOptions]);

  const pickerCenterLabel = t("_dashboard2_weekLabel", {
    week: pickerCenter.weekNumber,
    year: pickerCenter.year
  });

  const handlePrevious = useCallback(() => {
    setPickerCenter(previous =>
      previous.minus({ weeks: DASHBOARD2_WEEK_PICKER_STEP }).startOf("week")
    );
  }, []);

  const handleNext = useCallback(() => {
    setPickerCenter(previous =>
      previous.plus({ weeks: DASHBOARD2_WEEK_PICKER_STEP }).startOf("week")
    );
  }, []);

  const handleSelectWeek = useCallback(
    (week: DateTime) => {
      onSelectWeek(week.startOf("week"));
      onClose();
    },
    [onClose, onSelectWeek]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      data-testid="dashboard2-week-picker-dialog"
    >
      <DialogTitle>{t("_dashboard2_weekPickerTitle")}</DialogTitle>
      <DialogContent>
        <Box sx={dashboard2WeekPickerNavSx}>
          <IconButton
            aria-label={t("_dashboard2_weekPickerPrevious")}
            onClick={handlePrevious}
            data-testid="dashboard2-week-picker-previous"
            sx={dashboard2IconTouchTargetSx}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="subtitle1"
            component="p"
            sx={dashboard2WeekPickerCenterLabelSx}
            data-testid="dashboard2-week-picker-center-label"
          >
            {pickerCenterLabel}
          </Typography>
          <IconButton
            aria-label={t("_dashboard2_weekPickerNext")}
            onClick={handleNext}
            data-testid="dashboard2-week-picker-next"
            sx={dashboard2IconTouchTargetSx}
          >
            <ChevronRight />
          </IconButton>
        </Box>
        <List sx={dashboard2WeekPickerListSx} disablePadding>
          {weekOptions.map(week => {
            const weekKey = week.toISODate() ?? week.toISO();
            const isSelected = isSameDashboard2Week(week, weekAnchor);

            return (
              <ListItemButton
                key={weekKey}
                ref={weekKey === scrollTargetWeekKey ? scrollTargetRef : undefined}
                selected={isSelected}
                onClick={() => handleSelectWeek(week)}
                sx={dashboard2WeekPickerOptionSx(theme)}
                data-testid={`dashboard2-week-picker-option-${weekKey}`}
              >
                <ListItemText
                  primary={t("_dashboard2_weekLabel", {
                    week: week.weekNumber,
                    year: week.year
                  })}
                  secondary={
                    week.toFormat("d LLL") + " – " + week.endOf("week").toFormat("d LLL yyyy")
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};
